import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { parseContentAddress, type DocumentVersionRef } from "../../../shared/references/contentReferences";
import { appPaths } from "../../../shared/routing/appPaths";
import { segmentDisplayName } from "./documentNames";
import { useDocumentName } from "./useSourceDocument";

/**
 * A document version as people read it (`useDocumentName`), linking to that exact version.
 * A reference to one part of it (`<id>@<version>#<segment path>`) adds the part's name,
 * "10700441, Head", and links to that part. Until the name is read, or if it cannot be,
 * "Document" stands in. The raw reference shows nowhere, not even on hover (F77, F78, F94, F163).
 */
export default function DocumentVersionName({ reference }: { reference: string }) {
  const { t } = useTranslation();
  const address = parseContentAddress(reference);
  const document = address?.document;
  const version: DocumentVersionRef | null = document?.version == null ? null : { documentId: document.documentId, version: document.version };
  const segmentPath = address?.segmentPath ?? null;
  const documentName = useDocumentName(version) ?? t("sources.document.unnamed");
  const name = segmentPath === null ? documentName : `${documentName}, ${segmentDisplayName(segmentPath)}`;
  if (version === null) {
    return <span>{name}</span>;
  }
  return (
    <Link to={appPaths.sourceDocument(version, segmentPath)} className="app-link underline">
      {name}
    </Link>
  );
}
